import { useState, useRef, useCallback, useEffect } from "react";
import { useLogout } from "@hooks/useLogout";

/**
 * Custom hook for handling click outside events
 */
const useClickOutside = (ref: React.RefObject<HTMLDivElement | null>, handler: () => void, active: boolean) => {
	useEffect(() => {
		if (!active) return;

		const handleClick = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				handler();
			}
		};

		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [ref, handler, active]);
};

/**
 * Custom hook for profile dropdown functionality
 * @returns Object containing dropdown state and handlers
 */
export const useProfileDropdown = () => {
	const [showMenu, setShowMenu] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);
	const [showProfileModal, setShowProfileModal] = useState(false);

	// Get logout functionality from useLogout hook
	const { handleLogout, handleLogoutConfirm, handleLogoutCancel, showLogoutModal } = useLogout();

	const handleToggle = useCallback(() => setShowMenu(prev => !prev), []);
	const handleClose = useCallback(() => setShowMenu(false), []);

	useClickOutside(menuRef, handleClose, showMenu);

	const ShowProfile = () => {
		setShowProfileModal(true);
	};

	const HideProfile = () => {
		setShowProfileModal(false);
	};

	return {
		showMenu,
		menuRef,
		handleToggle,
		handleLogout,
		handleLogoutConfirm,
		handleLogoutCancel,
		showLogoutModal,
		ShowProfile,
		HideProfile,
		showProfileModal,
	};
};
